import { FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { envBoolean } from 'src/helpers/utils';
import {
  clearTurnstileFailure,
  isTurnstileEnabled,
  registerTurnstileHandle,
  setTurnstileFailed,
  setTurnstileToken,
  TurnstileHandle,
} from 'src/services/turnstileService';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { RootState } from 'src/types/store-types';
import Turnstile, { SCRIPT_UNAVAILABLE } from './Turnstile';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY?.trim() || '';
/** Retryable is not infinite: after this many failed challenges the forms stop waiting. */
const MAX_CHALLENGE_FAILURES = 10;
/** Change-password re-authenticates through /authenticatePlayer, so it needs a token just like login.
 *  AccountModal picks its view from subSection, not section — section is the parent ('my_account'). */
const CHANGE_PASSWORD_SECTION = 'change_password';

/** Rendering the widget starts a challenge, so it only appears where a token will actually be spent. */
const useIsAuthFlowOpen = (): boolean => {
  const { pathname } = useLocation();
  const { modalContent } = useSelector((state: RootState) => state.authModal);
  const { dialogs } = useSelector((state: RootState) => state.dialog);
  const { subSection } = useSelector((state: RootState) => state.accountModal);
  const { user } = useSelector((state: RootState) => state.user);

  const hasLoginPage = envBoolean(process.env.REACT_APP_HAS_LOGIN_PAGE as string) && !user;

  return (
    Boolean(modalContent) ||
    hasLoginPage ||
    pathname.includes('signup') ||
    subSection === CHANGE_PASSWORD_SECTION ||
    Boolean(dialogs?.[EnumDialogsKeys.TERMS_NOT_ACCEPTED])
  );
};

/** The one app-wide widget: solves as soon as it appears, so a token is waiting before it is needed. */
const GlobalTurnstile: FC = () => {
  const turnstileRef = useRef<TurnstileHandle>(null);
  const failureCountRef = useRef<number>(0);
  const isAuthFlowOpen = useIsAuthFlowOpen();

  // Re-runs when the widget appears: the ref is only populated once Turnstile is actually rendered.
  useEffect(() => {
    if (isAuthFlowOpen) {
      // Fresh widget, fresh budget — otherwise an earlier flow's failures carry over to this one.
      failureCountRef.current = 0;
      clearTurnstileFailure();
    }

    registerTurnstileHandle(turnstileRef.current);

    return () => registerTurnstileHandle(null);
  }, [isAuthFlowOpen]);

  const handleToken = (token: string | null): void => {
    if (token) {
      failureCountRef.current = 0;
    }

    setTurnstileToken(token);
  };

  const handleError = (errorCode: string): void => {
    // A 300*** challenge failure is retryable and Turnstile retries it itself, so callers keep waiting
    // rather than giving up on the first one. Only a script that never arrived is final immediately.
    failureCountRef.current += 1;

    const hasGivenUp = errorCode === SCRIPT_UNAVAILABLE || failureCountRef.current >= MAX_CHALLENGE_FAILURES;

    if (hasGivenUp) {
      // Retries are expected on a slow link and Cloudflare logs each one itself: only report the end.
      console.warn(`[Turnstile] gave up after ${failureCountRef.current} challenge(s) (${errorCode})`);
      setTurnstileFailed();
    }
  };

  if (!isTurnstileEnabled() || !isAuthFlowOpen) {
    return null;
  }

  return <Turnstile ref={turnstileRef} siteKey={SITE_KEY} onTokenChange={handleToken} onError={handleError} />;
};

export default GlobalTurnstile;
